import React, { useState, useEffect, useRef } from "react";
import { PlatformZone } from "./PlatformZone";
import { platformApiService } from "../../services/platformApi";

interface PlatformZonesProps {
 pageSlug: string;
 excludeZones?: string[]; // Zones to exclude from rendering
 containerClassName?: string; // Optional className for container
}

// Module-level cache for zone names to prevent flickering on navigation
const zoneNamesCache = new Map<string, string[]>();

/**
 * Component that dynamically renders zones for a platform page based on the stored zone order
 */
export const PlatformZones: React.FC<PlatformZonesProps> = ({ 
 pageSlug, 
 excludeZones = [],
 containerClassName 
}) => {
 const cacheKey = `${pageSlug}:${excludeZones.join(",")}`;
 const cachedZoneNames = zoneNamesCache.get(cacheKey);
 const hasCachedZoneNames = cachedZoneNames !== undefined;
 
 const [zoneNames, setZoneNames] = useState<string[]>(hasCachedZoneNames ? cachedZoneNames : []);
 const [loading, setLoading] = useState(!hasCachedZoneNames);
 const isFetchingRef = useRef<boolean>(false);

 useEffect(() => {
  // If we have cached zone names, use them immediately and fetch updates in background
  if (hasCachedZoneNames && cachedZoneNames) {
   setZoneNames(cachedZoneNames);
   setLoading(false);
   
   // Fetch updates in background without blocking render
   if (!isFetchingRef.current) {
    isFetchingRef.current = true;
    platformApiService.getPlatformPage(pageSlug)
     .then((response) => {
      const page = response.data.page;
      if (page?.zones) {
       let zones: string[] = [];
       if (page.zoneNames && page.zoneNames.length > 0) {
        zones = page.zoneNames;
       } else {
        zones = Object.keys(page.zones);
       }
       const filteredZones = zones.filter(zone => !excludeZones.includes(zone));
       
       // Only update if different
       if (JSON.stringify(filteredZones) !== JSON.stringify(cachedZoneNames)) {
        zoneNamesCache.set(cacheKey, filteredZones);
        setZoneNames(filteredZones);
       }
      }
     })
     .catch((error) => {
      console.error("Error fetching page data:", error);
     })
     .finally(() => {
      isFetchingRef.current = false;
     });
   }
   return;
  }

  // No cache - fetch immediately
  if (isFetchingRef.current) {
   return;
  }

  const fetchPageData = async () => {
   try {
    isFetchingRef.current = true;
    setLoading(true);
    const response = await platformApiService.getPlatformPage(pageSlug);
    const page = response.data.page;
    
    if (page?.zones) {
     // Use zoneNames if available, otherwise fall back to Object.keys
     let zones: string[] = [];
     if (page.zoneNames && page.zoneNames.length > 0) {
      zones = page.zoneNames;
     } else {
      zones = Object.keys(page.zones);
     }
     // Filter out excluded zones
     const filteredZones = zones.filter(zone => !excludeZones.includes(zone));
     zoneNamesCache.set(cacheKey, filteredZones);
     setZoneNames(filteredZones);
    }
   } catch (error) {
    console.error("Error fetching page data:", error);
    // On error, try to render zones in default order
    setZoneNames([]);
   } finally {
    setLoading(false);
    isFetchingRef.current = false;
   }
  };

  fetchPageData();
 }, [pageSlug, excludeZones.join(","), cacheKey, hasCachedZoneNames, cachedZoneNames]);

 if (loading) {
  return null; // Or a loading spinner
 }

 if (zoneNames.length === 0) {
  return null;
 }

 const content = (
  <>
   {zoneNames.map((zoneName) => (
    <PlatformZone key={zoneName} pageSlug={pageSlug} zoneName={zoneName} />
   ))}
  </>
 );

 if (containerClassName) {
  return <div className={containerClassName}>{content}</div>;
 }

 return content;
};

